import { useState, useEffect } from 'react'
import { 
  CheckCircle, 
  Clock, 
  Eye, 
  Star, 
  User, 
  Mail, 
  Phone, 
  FileText, 
  Calendar,
  DollarSign,
  MapPin,
  Building,
  Edit3,
  Upload,
  AlertCircle,
  Loader
} from 'lucide-react'
import { apiRequest } from '@/config/api'

interface ApplicationStatusProps {
  applicationId: number
  positionTitle: string
  companyName: string
  onClose: () => void
}

interface Application {
  id: number
  status: string
  applied_at: string
  last_updated: string
  recruiter_notes?: string
  candidate: {
    first_name: string
    last_name: string
    email: string
    phone?: string
    current_title?: string
    current_company?: string
    current_location?: string
    total_experience_years?: number
    expected_salary?: number
    expected_salary_currency?: string
    resume_url?: string
    linkedin_url?: string
    portfolio_url?: string
    summary?: string
  }
  cover_letter: string
}

const STATUS_CONFIG = {
  applied: {
    icon: Clock,
    color: 'bg-blue-100 text-blue-800',
    label: 'Application Submitted',
    description: 'Your application has been received and is being reviewed'
  },
  seen: {
    icon: Eye,
    color: 'bg-yellow-100 text-yellow-800',
    label: 'Under Review',
    description: 'A recruiter has viewed your application'
  },
  shortlisted: {
    icon: Star,
    color: 'bg-green-100 text-green-800',
    label: 'Shortlisted',
    description: 'Congratulations! You have been shortlisted for the next round'
  },
  interview_scheduled: {
    icon: Calendar,
    color: 'bg-purple-100 text-purple-800',
    label: 'Interview Scheduled',
    description: 'An interview has been scheduled - check your email'
  },
  rejected: {
    icon: AlertCircle,
    color: 'bg-red-100 text-red-800',
    label: 'Not Selected',
    description: 'Thank you for your interest. We will keep your profile for future opportunities'
  }
}

export default function ApplicationStatus({ 
  applicationId, 
  positionTitle, 
  companyName, 
  onClose 
}: ApplicationStatusProps) {
  const [application, setApplication] = useState<Application | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [editMode, setEditMode] = useState(false)
  const [updating, setUpdating] = useState(false)

  useEffect(() => {
    loadApplication()
  }, [applicationId])

  const loadApplication = async () => {
    try {
      setLoading(true)
      setError('')

      const response = await apiRequest(`applicants/application/${applicationId}`)
      const data = await response.json()

      if (response.ok && data.success) {
        setApplication(data.data.application)
      } else {
        setError(data.message || 'Failed to load application details')
      }
    } catch (err: any) {
      setError('Failed to load application. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateApplication = async (updatedData: Partial<Application['candidate']>) => {
    try {
      setUpdating(true)
      setError('')

      const response = await apiRequest(`applicants/application/${applicationId}`, {
        method: 'PUT',
        body: JSON.stringify(updatedData)
      })

      const data = await response.json()

      if (response.ok && data.success) {
        await loadApplication() // Reload the updated data
        setEditMode(false)
      } else {
        setError(data.message || 'Failed to update application')
      }
    } catch (err: any) {
      setError('Failed to update application. Please try again.')
    } finally {
      setUpdating(false)
    }
  }

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6">
          <div className="text-center">
            <Loader className="w-8 h-8 animate-spin mx-auto text-blue-600" />
            <p className="mt-2 text-gray-600">Loading your application...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !application) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-md w-full p-6">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Unable to Load Application</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={onClose}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    )
  }

  const statusConfig = STATUS_CONFIG[application.status as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.applied
  const StatusIcon = statusConfig.icon

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">{positionTitle}</h1>
              <p className="text-blue-100">{companyName}</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 p-2"
            >
              ✕
            </button>
          </div>

          {/* Status Badge */}
          <div className="mt-4 flex items-center space-x-3">
            <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusConfig.color}`}>
              <StatusIcon className="w-4 h-4 mr-2" />
              {statusConfig.label}
            </div>
            <span className="text-blue-100 text-sm">
              Applied on {new Date(application.applied_at).toLocaleDateString()}
            </span>
          </div>
          
          <p className="mt-2 text-blue-100 text-sm">{statusConfig.description}</p>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Recruiter Notes */}
          {application.recruiter_notes && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h3 className="font-semibold text-yellow-800 mb-2">Message from Recruiter</h3>
              <p className="text-yellow-700">{application.recruiter_notes}</p>
            </div>
          )}

          {/* Candidate Details */}
          <div className="bg-gray-50 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Your Application Details</h2>
              <button
                onClick={() => setEditMode(!editMode)}
                className="flex items-center space-x-2 text-blue-600 hover:text-blue-700"
              >
                <Edit3 className="w-4 h-4" />
                <span>{editMode ? 'Cancel Edit' : 'Update Details'}</span>
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <div className="flex items-center mt-1">
                    <User className="w-4 h-4 text-gray-400 mr-2" />
                    <span>{application.candidate.first_name} {application.candidate.last_name}</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <div className="flex items-center mt-1">
                    <Mail className="w-4 h-4 text-gray-400 mr-2" />
                    <span>{application.candidate.email}</span>
                  </div>
                </div>

                {application.candidate.phone && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Phone</label>
                    <div className="flex items-center mt-1">
                      <Phone className="w-4 h-4 text-gray-400 mr-2" />
                      <span>{application.candidate.phone}</span>
                    </div>
                  </div>
                )}

                {application.candidate.current_location && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Location</label>
                    <div className="flex items-center mt-1">
                      <MapPin className="w-4 h-4 text-gray-400 mr-2" />
                      <span>{application.candidate.current_location}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Professional Info */}
              <div className="space-y-4">
                {application.candidate.current_title && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Current Title</label>
                    <div className="flex items-center mt-1">
                      <Building className="w-4 h-4 text-gray-400 mr-2" />
                      <span>{application.candidate.current_title}</span>
                    </div>
                  </div>
                )}

                {application.candidate.current_company && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Current Company</label>
                    <span className="block mt-1">{application.candidate.current_company}</span>
                  </div>
                )}

                {application.candidate.total_experience_years && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Experience</label>
                    <span className="block mt-1">{application.candidate.total_experience_years} years</span>
                  </div>
                )}

                {application.candidate.expected_salary && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Expected Salary</label>
                    <div className="flex items-center mt-1">
                      <DollarSign className="w-4 h-4 text-gray-400 mr-2" />
                      <span>
                        {application.candidate.expected_salary_currency} {application.candidate.expected_salary.toLocaleString()}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Links */}
            <div className="mt-6 space-y-2">
              {application.candidate.resume_url && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Resume</label>
                  <a 
                    href={application.candidate.resume_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700 flex items-center mt-1"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    View Resume
                  </a>
                </div>
              )}

              {application.candidate.linkedin_url && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">LinkedIn</label>
                  <a 
                    href={application.candidate.linkedin_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700 block mt-1"
                  >
                    {application.candidate.linkedin_url}
                  </a>
                </div>
              )}

              {application.candidate.portfolio_url && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Portfolio</label>
                  <a 
                    href={application.candidate.portfolio_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700 block mt-1"
                  >
                    {application.candidate.portfolio_url}
                  </a>
                </div>
              )}
            </div>

            {/* Summary */}
            {application.candidate.summary && (
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700">Professional Summary</label>
                <p className="mt-1 text-gray-600">{application.candidate.summary}</p>
              </div>
            )}
          </div>

          {/* Cover Letter */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Cover Letter</h3>
            <p className="text-gray-700 whitespace-pre-wrap">{application.cover_letter}</p>
          </div>

          {/* Actions */}
          <div className="flex justify-center space-x-4">
            <button
              onClick={() => setEditMode(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg flex items-center space-x-2"
            >
              <Edit3 className="w-4 h-4" />
              <span>Update Application</span>
            </button>
            <button
              onClick={onClose}
              className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-6 py-2 rounded-lg"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}